import { useContext, useEffect, useState } from 'react';
import { Dropdown, Form, Modal, Spinner } from 'react-bootstrap';

import OARangePicker from '../../../components/OARangePicker';
import OAToolbar from '../../../components/widgets/OAToolbar';
import FlightContext, { ReportType } from '../context/FlightContext';
import { FlightOrderHistory } from './flightOrderHistory';
import BreadcrumbItem from './BreadcrumbItem';

interface IFlightQueryParams {
  data: any;
}
export const FlightsDashboard = (props: IFlightQueryParams) => {
  const { queryParams, setQueryParams } = props?.data;
  const { getFlightOrderList, downloadReport, report } = useContext(FlightContext);

  const [list, setList] = useState<Array<any>>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [dateError, setDateError] = useState<string>('');

  const claimList = () => {
    setIsLoading(true);
    getFlightOrderList(res => {
      setIsLoading(false);
      setList(res);
      setTotalPage(res?.totalPages ?? 0);
    });
  };

  useEffect(() => {
    if (queryParams?.dateRange?.[0] && queryParams?.dateRange?.[1]) {
      claimList();
    }
  }, [queryParams]);

  const onChangeRangePicker = (range: any) => {
    setQueryParams((prev: any) => ({ ...prev, dateRange: range, pageIndex: 1 }));
  };

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isDateRangeValid = (from: string, to: string) => {
    if (!from || !to) return false;
    const start = new Date(from);
    const end = new Date(to);
    if (end < start) return false;
    const threeMonthsLater = new Date(start);
    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
    return end <= threeMonthsLater;
  };

  const resetModal = () => {
    setShowEmailModal(false);
    setEmail('');
    setEmailError('');
    setFromDate('');
    setToDate('');
    setDateError('');
  };
  return (
    <>
      <OAToolbar
        title={'Flights'}
        breadcrumb={BreadcrumbItem()}
        actionsElement={
          <div className='d-flex'>
            <div className='me-4'>
              <OARangePicker
                className='dashboard-data-range-filter'
                name='dashboard-data-range'
                onChangeRangePicker={onChangeRangePicker}
                dateRange={queryParams?.dateRange}
                maxDate={new Date()}
              />
            </div>
            <div className='ms-4'>
              <Dropdown align='end'>
                <Dropdown.Toggle
                  variant='primary'
                  size='sm'
                  className='btn-flex fw-bolder'
                  disabled={report?.isLoading}
                >
                  {report?.isLoading ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-file-earmark-arrow-down me-2'></i>
                      Reports
                    </>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu className='py-2' style={{ minWidth: '220px' }}>
                  <Dropdown.Header className='text-muted fw-bold fs-8 px-4'>
                    SELECT REPORT TYPE
                  </Dropdown.Header>
                  <Dropdown.Item
                    onClick={() => downloadReport('booking_analytics')}
                    disabled={report?.isLoading}
                    className='px-4 py-2'
                  >
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-graph-up text-primary me-3 fs-5'></i>
                      <div>
                        <div className='fw-normal'>Booking Analytics</div>
                      </div>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => downloadReport('cancellation')}
                    disabled={report?.isLoading}
                    className='px-4 py-2'
                  >
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-x-circle text-danger me-3 fs-5'></i>
                      <div>
                        <div className='fw-normal'>Cancellation Report</div>
                      </div>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => downloadReport('internal')}
                    disabled={report?.isLoading}
                    className='px-4 py-2'
                  >
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-file-earmark-spreadsheet text-success me-3 fs-5'></i>
                      <div>
                        <div className='fw-normal'>Internal Report</div>
                      </div>
                    </div>
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => setShowEmailModal(true)}
                    disabled={report?.isLoading}
                    className='px-4 py-2'
                  >
                    <div className='d-flex align-items-center'>
                      <i className='bi bi-envelope text-info me-3 fs-5'></i>
                      <div>
                        <div className='fw-normal'>Email Report</div>
                      </div>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        }
      />
      <FlightOrderHistory
        list={list ?? []}
        totalPage={totalPage}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        onChangeRangePicker={onChangeRangePicker}
        loading={isLoading}
      />

      <Modal show={showEmailModal} onHide={resetModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Email Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className='mb-3'>
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type='email'
              placeholder='Enter email address'
              value={email}
              isInvalid={!!emailError}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
            />
            <Form.Control.Feedback type='invalid'>
              {emailError}
            </Form.Control.Feedback>
          </Form.Group>
          <div className='d-flex gap-3 mb-3'>
            <Form.Group className='flex-fill'>
              <Form.Label>From Date</Form.Label>
              <Form.Control
                type='date'
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  if (dateError) setDateError('');
                }}
              />
            </Form.Group>
            <Form.Group className='flex-fill'>
              <Form.Label>To Date</Form.Label>
              <Form.Control
                type='date'
                value={toDate}
                min={fromDate || undefined}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  setToDate(e.target.value);
                  if (dateError) setDateError('');
                }}
              />
            </Form.Group>
          </div>
          {dateError && (
            <div className='text-danger fs-7 mb-2'>{dateError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className='btn btn-secondary btn-sm' onClick={resetModal}>
            Cancel
          </button>
          <button
            className='btn btn-primary btn-sm'
            disabled={!email?.trim() || !fromDate || !toDate || report?.isLoading}
            onClick={() => {
              const trimmedEmail = email.trim();
              let hasError = false;

              if (!isValidEmail(trimmedEmail)) {
                setEmailError('Please enter a valid email address');
                hasError = true;
              }

              if (!isDateRangeValid(fromDate, toDate)) {
                setDateError('Date range cannot exceed 3 months');
                hasError = true;
              }

              if (hasError) return;

              downloadReport('email', trimmedEmail, fromDate, toDate);
              resetModal();
            }}
          >
            Submit
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
